<template>
	<Teleport to="body">
		<MultiButtonModal
			:show="showDiscardModal"
			icon="alert"
			title="Discard changes?"
			message="Are you sure? Any unsaved changes will be lost."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardModal(false) }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Discard', () => { discardChanges() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<AuthedLayout>
		<p class="dashboard-title">Settings</p>

		<!-- Personal Information -->
		<DefaultCard card-title="Personal Information" class="w-136" inner-padding="px-6 pt-6 pb-6" :title-divider-line="true">
			<form class="flex flex-col">
				<!-- First name -->
				<InputText class="w-full" label="First name" type="text" placeholder="First Name" autocomplete="given-name" v-model="first_name"
								  :errors="profileError" :error-field="'user.given_name'" icon="edit" :required="true" />

				<!-- Last name -->
				<InputText class="w-full" label="Last name" type="text" placeholder="Last Name" autocomplete="family-name" v-model="last_name"
							  :errors="profileError" :error-field="'user.family_name'" icon="edit" :required="true" />

				<!-- Email -->
				<InputText class="w-full" label="Email address" type="text" placeholder="Email Address" autocomplete="email" v-model="email"
							  :errors="profileError" :error-field="'user.email'" icon="edit" :required="true" />

				<!-- Phone Number -->
				<InputText label="Phone" class="w-full" type="tel" autocomplete="tel" placeholder="10-digit, U.S. only, for example (999) 999-9999"
							  v-model="phone_number" v-maska:phone_number_unmasked.unmasked="'(###) ###-####'"
							  :errors="profileError" :error-field="'user.phone_number'" icon="edit" />

				<!-- Two factor -->
				<InputText label="Two-factor authentication" class="w-full" type="text"
							  v-model="email"
							  :disabled="true"
				/>
				<!-- unused option to disable tfa option, check git history prior to to 2/17/26 for old layout -->
				<!--              <div v-if="tfaMethod"><button type="submit" @click.prevent="" class="h-fit bg-neutral text-black font-medium border border-neutral rounded-[4px] py-2 px-6 hover:bg-primary-subtlest my-1">Disable</button></div>-->
				<!--              <div v-else><button type="submit" @click.prevent="" class="h-fit bg-neutral text-black font-medium border border-neutral rounded-[4px] py-2 px-6 hover:bg-primary-subtlest my-1">Enable</button></div>-->

				<!-- Password -->
				<p v-if="!passwordLinkSent" class="mb-6 font-medium"><span class="cursor-pointer underline text-primary"
																							  @click="sendPasswordResetEmail">{{ isSendingResetPassword ? 'Sending...' : 'Reset Password' }}</span>
				</p>
				<p v-else class="mb-6 font-medium">Reset password email sent! Didn't get it? <span class="cursor-pointer underline text-primary"
																															  @click="sendPasswordResetEmail">{{ isSendingResetPassword ? 'Sending...' : 'Send it again' }}</span>.
				</p>

				<!-- Save/Cancel -->
				<div class="flex flex-col md:flex-row items-baseline justify-end md:space-x-4 ">
					<SubmitButton label="Cancel" type="outline" :disabled="isLoading" @click.prevent="changesMade()" />
					<SubmitButton label="Save" :loading="isLoading" @click.prevent="save" />
				</div>
				<ValidationError :errors="profileError" :field="null" />
			</form>

			<div class="flex flex-row justify-center text-center mt-6">
				<button @click="showTerms=true" class="clickable-text">View Terms and Conditions, and Privacy</button>
			</div>

			<Teleport to="html">
				<TermsPrivacyModal :is-open="showTerms" @close="showTerms = false" />
			</Teleport>
		</DefaultCard>
	</AuthedLayout>
</template>

<script setup lang="js">
import AuthedLayout from '@/layouts/AuthedLayout.vue'
import { ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { useAuthStore } from '@/stores/authStore.js'
import router from '@/router/router.js'
import { Toaster } from '@/models/utils/Toaster.js';
import { vMaska } from 'maska/vue'
import DefaultCard from '@/components/Forms/DefaultCard.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { userApi } from '@/api/User/UserApi.js'
import TermsPrivacyModal from '@/components/Modals/TermsPrivacyModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'

const authStore = useAuthStore()
const user = authStore.getAuth?.user()
const toaster = new Toaster();

const first_name = ref(user?.given_name)
const last_name = ref(user?.family_name)
const email = ref(user?.email)
const isLoading = ref(false)
const isSendingResetPassword = ref(false)
const showDiscardModal = ref(false)
const profileError = ref(null)
const passwordLinkSent = ref(false)
const phone_number = ref(user?.phone_number)
const phone_number_unmasked = ref('')
const showTerms = ref(false);

//TFA stuff, left for future
// const tfaMethod = ref(user?.tfa_method)
// const canAddTfa = ref(false)
//const receive_sms = ref(user?.receive_sms)

defineExpose({ phone_number_unmasked })

function save()
{
	isLoading.value = true

	userApi.update({
		'user': {
			'id': user.id,
			'given_name': first_name.value,
			'family_name': last_name.value,
			'phone_number': phone_number_unmasked.value,
			'email': email.value
		}
	}, (response) => {
		if (authStore.handleUpdateResult(response.body())) {
			profileError.value = null

			toaster.success('Your changes have been saved successfully!')

			isLoading.value = false
		}
	}, (e) =>
	{
		profileError.value = e.showErrorBanner()

		isLoading.value = false

	})
}

/**
 * Checks if any data has been modified when saving
 * @returns {boolean} True if changes, false if values unchanged
 */
function changesMade()
{
	if (user?.given_name !== first_name.value ||
		user?.family_name !== last_name.value ||
		user?.email !== email.value ||
		user?.phone_number.replace(/\s*-*\(*\)*/g, '') !== phone_number_unmasked.value
	)
	{
		toggleDiscardModal(true)
	}
	else
	{
		discardChanges()
	}
}

/**
 * Shows or hides the discard modal
 * @param p_show_eh {boolean} true to show, false to hide
 */
function toggleDiscardModal(p_show_eh)
{
	showDiscardModal.value = p_show_eh
}

/**
 * Take user back to previous page.
 */
function discardChanges()
{
	router.back()
}

/**
 * Initiates request to send reset password email
 */
function sendPasswordResetEmail()
{
	isSendingResetPassword.value = true
	passwordLinkSent.value = false

	userApi.forgotPassword({
		'user': {
			'email': user?.email
		}
	}, () => {
		isSendingResetPassword.value = false
		passwordLinkSent.value = true
		toaster.success('Password reset email sent!')
	}, (e) => {
		e.showErrorBanner()
	})
}

</script>